import { ItemStatusSliceType } from "../../../services/redux/slices/itemStatusSlice";
import {
  BogoSteps,
  CartLoyaltyV2SliceType,
} from "../../../services/redux/types/CartLoyaltyV2Types";
import {
  TransformOfferType,
  TransformOfferTypeObject,
} from "../../../services/redux/types/TypeHelper";
import { MAIN_OFFER_TYPE, OrderTypesEnum } from "../../enums";
import {
  DiscountedItemOfferType,
  FreeItemOfferType,
  ItemOfferV1Type,
  OfferType,
  RestaurantPackageOffer,
  SubItemType,
  reduxCartAndLoyalty,
} from "../../types";
import { getAllUnavailableItemIds } from "../itemDetails/itemDetails";

export const isValidOffer = (offer: ItemOfferV1Type | null) => {
  if (!offer) {
    return false;
  }

  const currDate = new Date().getTime();
  const from = new Date(offer.startedTime).getTime();
  const to = new Date(offer.endedTime).getTime();

  return from < currDate && to > currDate;
};

export const isItemOfferUnavailable = (
  itemStatusRedux: ItemStatusSliceType,
  offer: OfferType,
  orderType: OrderTypesEnum
) => {
  if (
    ![MAIN_OFFER_TYPE.DISCOUNT_ITEM, MAIN_OFFER_TYPE.FREE_ITEM].includes(
      offer.offerType
    )
  ) {
    return false;
  }

  const { allUnavailableItemIds } = getAllUnavailableItemIds(
    itemStatusRedux,
    orderType
  );

  const offerDetails = (
    offer as unknown as DiscountedItemOfferType | FreeItemOfferType
  ).details;

  if (
    allUnavailableItemIds.includes(offerDetails.subItem?.item?.id || 0) ||
    offerDetails.subItems?.every((subItem) =>
      allUnavailableItemIds.includes(subItem.item?.id!)
    )
  ) {
    return true;
  }

  return false;
};

export const getUnavailableOfferSubItems = (
  itemStatusRedux: ItemStatusSliceType,
  subItems: SubItemType[],
  orderType?: OrderTypesEnum
) => {
  const { allUnavailableItemIds, disabledItemsIds } = getAllUnavailableItemIds(
    itemStatusRedux,
    orderType
  );
  const unavailableOfferSubItemIds: number[] = [];
  const disabledSubItemsIds: number[] = [];

  for (const subItem of subItems) {
    if (allUnavailableItemIds.includes(subItem.item?.id!)) {
      unavailableOfferSubItemIds.push(subItem.id!);
    }
    if (disabledItemsIds.includes(subItem.item?.id!)) {
      disabledSubItemsIds.push(subItem.id!);
    }
  }

  return { unavailableOfferSubItemIds, disabledSubItemsIds };
};

export function allFreeItemsAndWithMinSpendZeroV2(
  cartAndLoyaltyReducer: TransformOfferType<reduxCartAndLoyalty>,
  cartLoyaltyV2: CartLoyaltyV2SliceType
): boolean {
  if (
    cartLoyaltyV2.cart.items?.length === 0 ||
    cartAndLoyaltyReducer.offersArray?.length === 0 ||
    !cartLoyaltyV2.cart.redeemedOffer
  ) {
    return false;
  }

  return cartLoyaltyV2.cart.isOfferApplied === true;
}

export const getDateWithTime = (restaurantDate: Date, time: string) => {
  const [startHour, startMinute] = time.split(":").map(Number);
  const date = new Date(restaurantDate);
  date.setHours(startHour, startMinute, 0, 0);

  return date;
};

export function groupByPoints(
  cartLoyaltyV2: CartLoyaltyV2SliceType,
  itemStatusRedux: ItemStatusSliceType,
  timeZone: string,
  orderTypeParam?: OrderTypesEnum,
  offers?: TransformOfferTypeObject<RestaurantPackageOffer[]>
): Array<RestaurantPackageOffer[]> {
  const grouped = new Map<
    number,
    {
      applicable: RestaurantPackageOffer[];
      notApplicable: RestaurantPackageOffer[];
    }
  >();
  if (!offers || !Array.isArray(offers)) {
    return [];
  }

  for (const offer of offers) {
    const points = offer?.pointingMilestone?.points ?? -1;
    const bucket = grouped.get(points) ?? { applicable: [], notApplicable: [] };

    const isApplicable = offer.packageOffer.isOfferApplicable({
      cart: cartLoyaltyV2.cart,
      itemStatusRedux,
      orderType: orderTypeParam,
      timeZone,
    });

    const hasDeletedItems = offer.packageOffer.hasDeletedItems();

    if (isApplicable && !hasDeletedItems) {
      bucket.applicable.push(offer as RestaurantPackageOffer);
    } else {
      bucket.notApplicable.push(offer as RestaurantPackageOffer);
    }

    grouped.set(points, bucket);
  }

  return Array.from(grouped.values()).map(({ applicable, notApplicable }) => [
    ...applicable,
    ...notApplicable,
  ]);
}

export const nextBogoStep = (bogoSteps?: BogoSteps) => {
  if (bogoSteps) {
    bogoSteps.currentStep += 1;
    if (bogoSteps.currentStep >= bogoSteps.totalSteps) {
      return undefined;
    }
  }

  return bogoSteps;
};

export const previousBogoStep = (bogoSteps?: BogoSteps) => {
  if (bogoSteps) {
    bogoSteps.currentStep -= 1;
    if (bogoSteps.currentStep < 0) {
      bogoSteps.currentStep = 0;
    }
    if (bogoSteps.currentStep === 0 && bogoSteps.totalSteps === 0) {
      return undefined;
    }
  }

  return bogoSteps;
};
